import {
  isSavedToFirebase,
  loadFilesFromFirebase,
  loadFromFirebase,
  saveFilesToFirebase,
  saveSceneToFirebaseForMigration,
  saveToFirebase,
} from "./firebase";
import {
  isSavedToHttpStorage,
  loadFilesFromHttpStorage,
  loadFromHttpStorage,
  saveFilesToHttpStorage,
  saveSceneForMigration as saveSceneToHttpStorageForMigration,
  saveToHttpStorage,
} from "./httpStorage";
import type { StorageBackend } from "./StorageBackend";

const firebaseStorage: StorageBackend = {
  isSaved: isSavedToFirebase,
  saveToStorageBackend: async (portal, elements, appState) => {
    // saveToFirebase returns either null or stored elements array; adapt to interface
    const res = await saveToFirebase(portal, elements, appState);
    if (!res) {
      return false;
    }
    return { reconciledElements: res };
  },
  loadFromStorageBackend: loadFromFirebase,
  saveFilesToStorageBackend: async ({ prefix, files }) => {
    // saveFilesToFirebase returns arrays; convert to Maps per interface
    const { savedFiles, erroredFiles } = await saveFilesToFirebase({
      prefix,
      files,
    });
    return {
      savedFiles: new Map(savedFiles.map((id) => [id, true] as const)),
      erroredFiles: new Map(erroredFiles.map((id) => [id, true] as const)),
    };
  },
  loadFilesFromStorageBackend: loadFilesFromFirebase,
  saveSceneForMigration: saveSceneToFirebaseForMigration,
};

const httpStorage: StorageBackend = {
  isSaved: isSavedToHttpStorage,
  saveToStorageBackend: saveToHttpStorage,
  loadFromStorageBackend: loadFromHttpStorage,
  saveFilesToStorageBackend: saveFilesToHttpStorage,
  loadFilesFromStorageBackend: loadFilesFromHttpStorage,
  saveSceneForMigration: saveSceneToHttpStorageForMigration,
};

// board.inboxrepetitor.ru doesn't persist scenes server-side (yet) — scene
// state lives only in the E2E-encrypted room via excalidraw-room, so saves
// are no-ops rather than errors.
const noopStorage: StorageBackend = {
  isSaved: () => true,
  saveToStorageBackend: async () => false,
  loadFromStorageBackend: async () => null,
  saveFilesToStorageBackend: async () => ({
    // callers (Collab.tsx's saveFiles) do `.reduce()` on these expecting
    // arrays, not Maps — see the fixed real bug this used to trigger
    savedFiles: [],
    erroredFiles: [],
  }),
  loadFilesFromStorageBackend: async () => ({
    loadedFiles: [],
    erroredFiles: new Map(),
  }),
  saveSceneForMigration: async () => {},
};

const storageBackends = new Map<string, StorageBackend>()
  .set("firebase", firebaseStorage)
  .set("http", httpStorage)
  .set("none", noopStorage);

export let storageBackend: StorageBackend | null = null;

export async function getStorageBackend() {
  if (storageBackend) {
    return storageBackend;
  }

  const storageBackendName = import.meta.env.VITE_APP_STORAGE_BACKEND || "none";

  if (storageBackends.has(storageBackendName)) {
    storageBackend = storageBackends.get(storageBackendName) as StorageBackend;
  } else {
    console.warn("No storage backend found, default to firebase");
    storageBackend = firebaseStorage;
  }

  return storageBackend;
}
